import React, { useState, useEffect } from 'react';
import { 
  FaHeart, 
  FaComment, 
  FaUserFriends, 
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaSearch,
  FaTimes,
  FaEye
} from 'react-icons/fa';
import api from '../../services/api';

const MatchesManagement = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, interest, message
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMatch, setSelectedMatch] = useState(null);

    useEffect(() => {
        fetchMatches();
    }, []);

    const fetchMatches = async () => {
        try {
            // ONLY CHANGE: Changed from '/admin/matches' to '/matches'
            const res = await api.get('/admin/matches');
            setMatches(res.data);
        } catch (error) {
            console.error('Failed to fetch matches', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeColor = (type) => {
        switch(type) {
            case 'interest': return 'bg-rose-500';
            case 'interest-accepted': return 'bg-emerald-500';
            case 'message': return 'bg-blue-500';
            default: return 'bg-gray-500';
        }
    };

    const getTypeIcon = (type) => {
        switch(type) {
            case 'interest': 
            case 'interest-accepted': 
                return <FaHeart className="text-white text-sm" />;
            case 'message': 
                return <FaComment className="text-white text-sm" />;
            default: 
                return <FaUserFriends className="text-white text-sm" />;
        }
    };

    const getTypeLabel = (type) => {
        return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

// temporarily disable filter check for interest
const filteredMatches = matches.filter(match => {
    return searchTerm === '' ||
           match.sender?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           match.receiver?.name?.toLowerCase().includes(searchTerm.toLowerCase());
});


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading matches...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Matches & Connections</h1>
                        <p className="text-gray-600 mt-1">Monitor user interactions and connections</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="px-4 py-2.5 bg-blue-500 text-white rounded-xl font-medium flex items-center">
                            <FaUserFriends className="mr-2" />
                            {matches.length} Total
                        </div>
                    </div>
                </div>

                {/* Filters & Search */}
                <div className="flex flex-col md:flex-row gap-4 mt-6">
                    {/* Search */}
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by user name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {searchTerm && (
                            <button 
                                onClick={() => setSearchTerm('')}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    {/* Filter Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                                filter === 'all' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            All Types
                        </button>
                        <button
                            onClick={() => setFilter('interest')}
                            className={`px-4 py-3 rounded-xl font-medium transition-colors flex items-center ${
                                filter === 'interest' 
                                    ? 'bg-rose-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <FaHeart className="mr-2" /> Interest
                        </button>
                        <button
                            onClick={() => setFilter('message')}
                            className={`px-4 py-3 rounded-xl font-medium transition-colors flex items-center ${
                                filter === 'message' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <FaComment className="mr-2" /> Messages
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Interactions</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{matches.length}</p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <FaUserFriends className="text-blue-500 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Interest Requests</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {matches.filter(m => m.messageType === 'interest').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
                            <FaHeart className="text-rose-500 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Messages</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {matches.filter(m => m.messageType === 'message').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <FaComment className="text-blue-500 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Accepted</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {matches.filter(m => m.messageType === 'interest-accepted').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                            <FaCheckCircle className="text-emerald-500 text-xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Matches Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Interaction</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Users</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Type</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Date & Time</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredMatches.length > 0 ? filteredMatches.map((match) => (
                                <tr 
                                    key={match._id} 
                                    className="hover:bg-gray-50/80 transition-colors duration-150 cursor-pointer"
                                    onClick={() => setSelectedMatch(match)}
                                >
                                    <td className="px-8 py-5">
                                        <div className="flex items-center space-x-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getTypeColor(match.messageType)}`}>
                                                {getTypeIcon(match.messageType)}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-800">{getTypeLabel(match.messageType)}</p>
                                                <p className="text-sm text-gray-500">Connection</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold mr-2">
                                                    {match.sender?.name?.[0]}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800">{match.sender?.name || 'Unknown'}</p>
                                                    <p className="text-xs text-gray-500">Initiator</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-rose-500 flex items-center justify-center text-white text-xs font-bold mr-2">
                                                    {match.receiver?.name?.[0]}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800">{match.receiver?.name || 'Unknown'}</p>
                                                    <p className="text-xs text-gray-500">Receiver</p>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                            match.messageType === 'interest' 
                                                ? 'bg-rose-50 text-rose-600' 
                                                : match.messageType === 'interest-accepted'
                                                ? 'bg-emerald-50 text-emerald-600'
                                                : 'bg-blue-50 text-blue-600'
                                        }`}>
                                            {getTypeLabel(match.messageType)}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="space-y-1">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <FaCalendarAlt className="mr-2 text-gray-400" />
                                                {new Date(match.createdAt).toLocaleDateString()}
                                            </div>
                                            <div className="flex items-center text-sm text-gray-600">
                                                <FaClock className="mr-2 text-gray-400" />
                                                {new Date(match.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                            match.messageType === 'interest-accepted'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}>
                                            {match.messageType === 'interest-accepted' ? 'Accepted' : 'Active'}
                                        </span>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-16">
                                        <div className="flex flex-col items-center">
                                            <FaUserFriends className="text-6xl text-gray-300 mb-4" />
                                            <p className="text-gray-600 text-lg font-medium">No interactions found</p>
                                            <p className="text-gray-500 mt-1">Try changing your search or filter</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Info */}
                <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center">
                        <p className="text-gray-600">
                            Showing <span className="font-semibold">{filteredMatches.length}</span> of <span className="font-semibold">{matches.length}</span> interactions
                        </p>
                        
                    </div>
                </div>
            </div>

            {/* Match Detail Modal */}
            {selectedMatch && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">Interaction Details</h2>
                                    <p className="text-gray-600 mt-1">View complete connection information</p>
                                </div>
                                <button
                                    onClick={() => setSelectedMatch(null)}
                                    className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                                >
                                    <FaTimes className="text-gray-500 text-xl" />
                                </button>
                            </div>
                        </div>

                        <div className="px-8 py-6 space-y-6">
                            {/* Type Badge */}
                            <div className="flex items-center justify-between">
                                <div className={`px-6 py-3 rounded-xl ${
                                    selectedMatch.messageType === 'interest' 
                                        ? 'bg-rose-50 text-rose-600' 
                                        : selectedMatch.messageType === 'interest-accepted'
                                        ? 'bg-emerald-50 text-emerald-600'
                                        : 'bg-blue-50 text-blue-600'
                                }`}>
                                    <div className="flex items-center">
                                        {getTypeIcon(selectedMatch.messageType)}
                                        <span className="ml-2 font-medium">{getTypeLabel(selectedMatch.messageType)}</span>
                                    </div>
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <FaCalendarAlt className="mr-2" />
                                    {new Date(selectedMatch.createdAt).toLocaleString()}
                                </div>
                            </div>

                            {/* Users */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-700 mb-3">Initiator</h3>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-lg font-bold mr-3">
                                            {selectedMatch.sender?.name?.[0]}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800">{selectedMatch.sender?.name || 'Unknown'}</p>
                                            <p className="text-sm text-gray-500">{selectedMatch.sender?.email || 'No email'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-700 mb-3">Receiver</h3>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-rose-500 flex items-center justify-center text-white text-lg font-bold mr-3">
                                            {selectedMatch.receiver?.name?.[0]}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800">{selectedMatch.receiver?.name || 'Unknown'}</p>
                                            <p className="text-sm text-gray-500">{selectedMatch.receiver?.email || 'No email'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Info */}
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h3 className="font-semibold text-gray-700 mb-3">Additional Information</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500">Interaction ID</p>
                                        <p className="font-medium text-gray-800">{selectedMatch._id}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Status</p>
                                        <p className={`font-medium ${
                                            selectedMatch.messageType === 'interest-accepted'
                                                ? 'text-emerald-600'
                                                : 'text-blue-600'
                                        }`}>
                                            {selectedMatch.messageType === 'interest-accepted' ? 'Accepted' : 'Active'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setSelectedMatch(null)}
                                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
                                >
                                    Close
                                </button>
                                <button
                                    className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center"
                                >
                                    <FaEye className="mr-2" /> View Profile
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MatchesManagement;